import React, { useState, useRef, useEffect } from 'react';
import { Bot, ChevronDown, CheckCircle2, AlertCircle, Loader2, Circle } from 'lucide-react';
import { useAgent } from '../context/AgentContext';

export interface AgentSelectorProps {
  className?: string;
  onAgentChange?: (slug: string) => void;
}

export function AgentSelector({ className = '', onAgentChange }: AgentSelectorProps) {
  const { agents, selectedAgent, defaultAgentSlug, loading, error, selectAgent } = useAgent();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const handleSelect = (slug: string) => {
    selectAgent(slug);
    onAgentChange?.(slug);
    setIsOpen(false);
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 px-2 py-1.5 ${className}`}>
        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
        <span className="text-sm text-gray-400">Loading agents...</span>
      </div>
    );
  }

  if (error || !selectedAgent) {
    return (
      <div className={`flex items-center gap-2 px-2 py-1.5 ${className}`}>
        <AlertCircle className="w-4 h-4 text-red-400" />
        <span className="text-sm text-red-500">{error || 'No agents'}</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen((o) => !o)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        data-testid="agent-selector-button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select agent"
      >
        {selectedAgent.healthy ? (
          <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
        ) : (
          <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
        )}

        <span className="font-semibold text-gray-800 dark:text-gray-100 text-base leading-tight">
          {selectedAgent.name}
          {selectedAgent.slug === defaultAgentSlug && (
            <span className="ml-1.5 text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
              default
            </span>
          )}
        </span>

        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl z-50 overflow-hidden"
          role="listbox"
          aria-label="Available agents"
        >
          <div className="py-1">
            {agents.map((agent) => {
              const isSelected = agent.slug === selectedAgent.slug;
              return (
                <button
                  key={agent.slug}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(agent.slug)}
                  className={`w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors ${
                    isSelected ? 'bg-sky-50 dark:bg-sky-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {agent.healthy ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <Circle className="w-4 h-4 text-gray-300" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`text-sm font-medium ${
                          isSelected ? 'text-sky-700 dark:text-sky-400' : 'text-gray-800 dark:text-gray-200'
                        }`}
                      >
                        {agent.name}
                      </span>
                      {agent.slug === defaultAgentSlug && (
                        <span className="text-[10px] font-medium px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          default
                        </span>
                      )}
                    </div>
                    {agent.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {agent.description}
                      </p>
                    )}
                  </div>

                  {isSelected && (
                    <Bot className="flex-shrink-0 w-4 h-4 text-sky-500 mt-0.5" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
